/**
 * カレンダーの保留トレイ
 *
 * 予定をいったんここへドロップ → 週を移動 → トレイからカレンダーへドラッグで再配置する。
 * トレイに置いた時点では API を呼ばないので、リロードすると元の位置に戻る。
 */

import { Draggable } from '@fullcalendar/interaction'
import { X } from 'lucide-react'
import { type RefObject, useEffect } from 'react'

import type { TodoResponse } from '@/api/types/todo'
import { cn } from '@/lib/utils'

import { DEFAULT_DURATION_MIN, getScheduledMinutes, PARKED_TODO_MIME } from './parking-tray-utils'
import { SCHEDULE_COLORS } from './schedule-items'

type ParkingTrayProps = {
  trayRef: RefObject<HTMLDivElement | null>
  todos: TodoResponse[]
  onRemove: (todoId: number) => void
  /** FullCalendar (週ビュー) へは専用の Draggable、日ビューへは HTML5 DnD で渡す */
  dragMode: 'fullcalendar' | 'native'
}

export const ParkingTray = ({ trayRef, todos, onRemove, dragMode }: ParkingTrayProps) => {
  useEffect(() => {
    const el = trayRef.current
    if (!el || dragMode !== 'fullcalendar') return
    const draggable = new Draggable(el, {
      itemSelector: '[data-parked-id]',
      minDistance: 5,
      eventData: (itemEl) => ({
        title: itemEl.dataset.title ?? '',
        duration: { minutes: Number(itemEl.dataset.minutes) || DEFAULT_DURATION_MIN },
        create: false,
      }),
    })
    return () => draggable.destroy()
  }, [trayRef, dragMode])

  return (
    // 高さ 0 の sticky ラッパーでレイアウトを動かさずにスクロール領域の下端へ浮かせる
    <div className="sticky bottom-0 z-30 h-0">
      <div
        ref={trayRef}
        className={cn(
          'absolute inset-x-2 bottom-2 rounded-lg border-2 border-dashed bg-background/95 p-2 shadow-lg backdrop-blur',
          'data-[dragging=true]:border-primary data-[dragging=true]:bg-primary/10',
          todos.length === 0 && 'hidden data-[dragging=true]:block',
        )}
      >
        <p className="mb-1 text-[0.7rem] text-muted-foreground">
          {todos.length === 0
            ? 'ここにドロップして保留 → 日付を移動してカレンダーへドラッグ'
            : '保留中: カレンダーへドラッグで配置 / × で元に戻す'}
        </p>
        {todos.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {todos.map((t) => (
              <div
                key={t.id}
                data-parked-id={t.id}
                data-title={t.title}
                data-minutes={getScheduledMinutes(t)}
                draggable={dragMode === 'native'}
                onDragStart={(e) => {
                  if (dragMode !== 'native') return
                  e.dataTransfer.setData(PARKED_TODO_MIME, String(t.id))
                  e.dataTransfer.effectAllowed = 'move'
                }}
                style={{ backgroundColor: t.is_event ? SCHEDULE_COLORS.personal : SCHEDULE_COLORS.todo }}
                className="flex max-w-full cursor-grab items-center gap-1 rounded-md px-2 py-1 text-xs text-white select-none"
              >
                <span className="truncate">
                  {t.is_event ? '📅' : '📝'} {t.title}
                </span>
                <button
                  type="button"
                  aria-label="保留を解除"
                  onClick={() => onRemove(t.id)}
                  className="shrink-0 rounded p-0.5 hover:bg-white/20"
                >
                  <X className="size-3" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
